import * as Sentry from '@sentry/angular';

import { WorkflowStepErrorResponse } from '@shared/models';
import { classifyApiFailure } from '@shared/utils/api-failure';

import { normalizeRoute } from './sentry.service';

export interface ApiFailure {
  status: number;
  method: string;
  url: string;
  /** The error response body: KOR's JSON, a plain string, or a ProgressEvent for a network failure */
  body: unknown;
  /** The app route the customer is on when the call fails (`router.url`) */
  route: string;
}

const LINX_REQUEST_STEP = /^\/linx-request\/:id\/(?<step>.+)$/u;

/** The step the customer is on, e.g. `sign-here` or `individual/customer-account`; other routes with ids as `:id` */
export const workflowStepOf = (route: string): string => {
  const normalized = normalizeRoute(route);
  return LINX_REQUEST_STEP.exec(normalized)?.groups?.['step'] ?? normalized;
};

const bodyFields = (body: unknown): WorkflowStepErrorResponse & { traceId?: string } =>
  body && typeof body === 'object' ? (body as WorkflowStepErrorResponse & { traceId?: string }) : {};

/**
 * Reports a failed API call the backend cannot report itself (KOR-15194): a Sentry event, a breadcrumb, or nothing, as
 * `classifyApiFailure` decides. Sends the endpoint with ids as `:id` and no query string, the status, the errorCode, the
 * workflow step and the traceId - never the response `message` or body, which can carry customer data. Never throws: a
 * reporting failure must not change what the caller sees.
 */
export const reportApiFailure = ({ status, method, url, body, route }: ApiFailure): void => {
  try {
    const kind = classifyApiFailure(status, body);
    if (kind === 'none') return;

    const endpoint = `${method} ${normalizeRoute(url)}`;
    const { errorCode, traceId } = bodyFields(body);

    if (kind === 'breadcrumb') {
      Sentry.addBreadcrumb({ category: 'api-failure', level: 'warning', message: endpoint, data: { status, endpoint, traceId } });
      return;
    }

    Sentry.captureMessage(`API failure: ${status} ${endpoint}`, {
      level: 'error',
      fingerprint: ['api-failure', String(status), endpoint, errorCode ?? '-'],
      tags: {
        apiStatus: String(status),
        apiEndpoint: endpoint,
        workflowStep: workflowStepOf(route),
        ...(errorCode ? { apiErrorCode: errorCode } : {}),
        // The join key to the backend's own event and logs for this request
        ...(traceId ? { traceId } : {}),
      },
    });
  } catch (error) {
    console.warn('Could not report a failed API call', error);
  }
};
